import React from 'react';
import { getScoreColor, getScoreBand, colors, KAYAKALP_PASS_SCORE } from '../../theme';

// ── Card ──────────────────────────────────────────────────────────────────────
export function Card({ children, className = '', style }) {
  return (
    <div style={{ background:'#fff', borderRadius:8, boxShadow:'0 1px 4px rgba(0,0,0,0.1)', padding:20, ...style }} className={className}>
      {children}
    </div>
  );
}

// ── Stat Card ─────────────────────────────────────────────────────────────────
export function StatCard({ icon, label, value, sub, color = colors.primary[700], bg }) {
  return (
    <div style={{ background: bg || '#fff', borderRadius:8, padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.08)', borderLeft:`4px solid ${color}` }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
        {icon && <span style={{ fontSize:22 }}>{icon}</span>}
        <span style={{ fontSize:13, color: colors.text.secondary }}>{label}</span>
      </div>
      <div style={{ fontSize:28, fontWeight:700, color, lineHeight:1.2 }}>{value}</div>
      {sub && <div style={{ fontSize:12, color: colors.text.secondary, marginTop:4 }}>{sub}</div>}
    </div>
  );
}

// ── Score Badge ───────────────────────────────────────────────────────────────
export function ScoreBadge({ pct, size = 'md' }) {
  const color   = getScoreColor(pct ?? 0);
  const band    = getScoreBand(pct ?? 0);
  const passing = (pct ?? 0) >= KAYAKALP_PASS_SCORE;
  const fontSize = size === 'lg' ? 32 : size === 'sm' ? 16 : 22;

  return (
    <div style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', gap:3 }}>
      <span style={{ fontSize, fontWeight:800, color }}>{pct !== null && pct !== undefined ? `${Math.round(pct)}%` : '—'}</span>
      <span style={{ fontSize:11, fontWeight:600, color, background: color+'18', padding:'2px 8px', borderRadius:99 }}>{band}</span>
      <span style={{ fontSize:10, fontWeight:700, color: passing ? colors.success[700] : colors.error[700], background: passing ? colors.success[50] : colors.error[50], padding:'2px 6px', borderRadius:99 }}>
        {passing ? 'PASS' : 'FAIL'}
      </span>
    </div>
  );
}

// ── Status Badge ──────────────────────────────────────────────────────────────
const STATUS_CFG = {
  FC: { label:'FC', bg: colors.success[50], color: colors.success[700] },
  PC: { label:'PC', bg: colors.warning[50], color: colors.warning[700] },
  NC: { label:'NC', bg: colors.error[50],   color: colors.error[700]   },
  NA: { label:'NA', bg: colors.neutral[100],color: colors.neutral[600] },
};
export function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.NA;
  return (
    <span style={{ background: cfg.bg, color: cfg.color, fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:99 }}>
      {cfg.label}
    </span>
  );
}

// ── Progress Bar ──────────────────────────────────────────────────────────────
export function ProgressBar({ pct, height = 8, showLabel = false }) {
  const color = getScoreColor(pct ?? 0);
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <div style={{ flex:1, height, background: colors.neutral[200], borderRadius:height, overflow:'hidden' }}>
        <div style={{ width:`${Math.min(pct ?? 0, 100)}%`, height:'100%', background: color, borderRadius:height, transition:'width 0.4s' }} />
      </div>
      {showLabel && <span style={{ fontSize:12, fontWeight:600, color, minWidth:36 }}>{Math.round(pct ?? 0)}%</span>}
    </div>
  );
}

// ── Alert Banner ──────────────────────────────────────────────────────────────
export function AlertBanner({ type = 'info', message, onClose }) {
  const cfg = {
    info:    { bg: colors.primary[50],  border: colors.primary[200],  color: colors.primary[800] },
    warning: { bg: colors.warning[50],  border: colors.warning[200],  color: colors.warning[800] },
    error:   { bg: colors.error[50],    border: colors.error[200],    color: colors.error[800]   },
    success: { bg: colors.success[50],  border: colors.success[200],  color: colors.success[800] },
  }[type];
  return (
    <div style={{ background: cfg.bg, border:`1px solid ${cfg.border}`, color: cfg.color, borderRadius:8, padding:'10px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:12 }}>
      <span style={{ fontSize:14 }}>{message}</span>
      {onClose && <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:16, color: cfg.color }}>×</button>}
    </div>
  );
}

// ── Section Header ────────────────────────────────────────────────────────────
export function SectionHeader({ title, subtitle, action }) {
  return (
    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:16 }}>
      <div>
        <h3 style={{ margin:0, fontSize:16, fontWeight:600, color: colors.text.primary }}>{title}</h3>
        {subtitle && <p style={{ margin:'2px 0 0', fontSize:13, color: colors.text.secondary }}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// ── Loading Spinner ───────────────────────────────────────────────────────────
export function Spinner({ size = 32 }) {
  return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', padding:32 }}>
      <div style={{ width:size, height:size, border:`3px solid ${colors.neutral[200]}`, borderTopColor: colors.primary[600], borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────────────────────
export function EmptyState({ icon = '📋', title, subtitle }) {
  return (
    <div style={{ textAlign:'center', padding:'48px 24px', color: colors.text.secondary }}>
      <div style={{ fontSize:48, marginBottom:12 }}>{icon}</div>
      <div style={{ fontSize:16, fontWeight:600, color: colors.text.primary, marginBottom:4 }}>{title}</div>
      {subtitle && <div style={{ fontSize:14 }}>{subtitle}</div>}
    </div>
  );
}
